import { ScanText } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Accept } from "react-dropzone";
import { useLocation, useNavigate } from "react-router-dom";

import { ProcessingOverlay } from "@/components/feedback/ProcessingOverlay";
import { Button } from "@/components/ui/button";
import { DropZone } from "@/components/upload/DropZone";
import { SampleButton } from "@/components/upload/SampleButton";
import { convertPptx } from "@/lib/api";
import { loadSampleFile } from "@/lib/sample";
import { useBundleStore } from "@/stores/bundleStore";
import { kindOf, useSourceStore } from "@/stores/sourceStore";

const ACCEPT: Accept = {
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "application/pdf": [".pdf"],
};
const MAX_BYTES = 50 * 1024 * 1024;

export function UploadSection() {
  const { isConverting, error, setBundle, setConverting, setError } = useBundleStore();
  const setFile = useSourceStore((s) => s.setFile);
  const sharedFile = useSourceStore((s) => s.file);
  const navigate = useNavigate();
  const location = useLocation();
  const autoRan = useRef(false);
  const [pdfName, setPdfName] = useState<string | null>(null);

  const convert = useCallback(
    async (file: File) => {
      setConverting(true);
      setError(null);
      try {
        setBundle(await convertPptx(file));
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unknown error");
      } finally {
        setConverting(false);
      }
    },
    [setBundle, setConverting, setError],
  );

  const handleFile = useCallback(
    async (file: File) => {
      setFile(file);
      if (kindOf(file) === "pdf") {
        setPdfName(file.name); // PDF has no JSON decompose — route to layout analysis
        return;
      }
      setPdfName(null);
      await convert(file);
    },
    [setFile, convert],
  );

  // Auto-run on arrival via navigation state:
  //  - autoConvert: "JSON ビューアで開く" from /layout → convert the shared PPTX.
  //  - autoSample:  "サンプルで試す" from the landing page → load & convert the demo deck.
  useEffect(() => {
    if (autoRan.current) return;
    const st = location.state as { autoConvert?: boolean; autoSample?: boolean } | null;
    if (st?.autoConvert && sharedFile && kindOf(sharedFile) === "pptx") {
      autoRan.current = true;
      void convert(sharedFile);
      return;
    }
    if (st?.autoSample) {
      autoRan.current = true;
      void (async () => {
        try {
          await handleFile(await loadSampleFile());
        } catch (e) {
          setError(e instanceof Error ? e.message : "サンプルの読み込みに失敗しました");
        }
      })();
    }
  }, [location.state, sharedFile, convert, handleFile, setError]);

  return (
    <section className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <h1 className="mb-3 font-display text-3xl font-medium sm:text-4xl">アップロード</h1>
      <p className="mb-2 text-[var(--color-ink-muted)]">
        <code className="mx-1 rounded px-1 font-mono text-[var(--color-ink)]">.pptx</code>
        は構造化 JSON に分解し、ブラウザで閲覧・コピー・復元できます。
        <code className="mx-1 rounded px-1 font-mono text-[var(--color-ink)]">.pdf</code>
        はレイアウト解析でページ構造を可視化し、PowerPoint を新規生成できます。
      </p>
      <p className="mb-8 text-sm text-[var(--color-ink-muted)]">
        アップロードしたファイルはサーバに保存されず、メモリ上で処理されます。ここで読み込んだファイルは、そのままレイアウト解析でも利用できます。
      </p>
      <DropZone
        onFile={handleFile}
        disabled={isConverting}
        accept={ACCEPT}
        maxSizeBytes={MAX_BYTES}
        title=".pptx / .pdf をドラッグ＆ドロップ"
        hint="またはクリックで選択 · 最大 50 MB"
      />

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-sm text-[var(--color-ink-muted)]">
          手元に資料がない場合は、用意したサンプル（事業提案スライド・9 枚）で試せます。
        </span>
        <SampleButton onSample={handleFile} disabled={isConverting} size="sm" />
      </div>

      {pdfName && !isConverting && (
        <div className="mt-4 rounded-[12px] border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-4">
          <p className="mb-3 text-sm text-[var(--color-ink-muted)]">
            PDF「<span className="text-[var(--color-ink)]">{pdfName}</span>
            」を読み込みました。PDF はレイアウト解析でご利用いただけます。
          </p>
          <Button onClick={() => navigate("/layout", { state: { autoAnalyze: true } })}>
            <ScanText size={16} />
            レイアウト解析で開く
          </Button>
        </div>
      )}

      {isConverting && (
        <ProcessingOverlay
          message="変換しています…"
          detail="PowerPoint をスライドごとの JSON に分解しています"
        />
      )}
      {error && !isConverting && (
        <div
          role="alert"
          className="mt-4 rounded-[12px] border border-[var(--color-coral)] bg-[var(--color-coral-soft)]/30 px-4 py-3 text-sm text-[var(--color-coral-dark)]"
        >
          <p className="mb-1 font-display font-medium">変換に失敗しました</p>
          <p className="break-words">{error}</p>
        </div>
      )}

      <div className="mt-8 grid gap-4 rounded-[14px] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4 text-sm text-[var(--color-ink-muted)] sm:grid-cols-3 sm:p-5">
        <div>
          <div className="mb-1 font-display text-[var(--color-ink)]">1. 変換 / 解析</div>
          <div>PPTX はスライドごとに JSON 化、PPTX / PDF はページのレイアウトを解析します。</div>
        </div>
        <div>
          <div className="mb-1 font-display text-[var(--color-ink)]">2. 眺める・渡す</div>
          <div>階層ビューアや色分けの境界ボックスで確認、コピーして AI に編集させられます。</div>
        </div>
        <div>
          <div className="mb-1 font-display text-[var(--color-ink)]">3. 戻す・生成</div>
          <div>編集 JSON から .pptx を復元、PDF からは PowerPoint を新規生成できます。</div>
        </div>
      </div>
    </section>
  );
}
